import { useGSAP } from '@gsap/react';
import { graphql } from 'gatsby';
import gsap from 'gsap';
import React, { useRef } from 'react';
import { ModuleBgColor } from '../../../templates/Page';
import { RawPortableText } from '../../../types/types';
import { getPortableTextAsString } from '../../../utils/sanity';
import { buildFaqSchema, JsonLd } from '../../../utils/structuredData';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import Accordion from '../Accordion';
import GenericPortableText from '../GenericPortableText';
import ModuleLayout from '../ModuleLayout';
import * as styles from './AccordionModule.module.scss';

export const AccordionModuleFragment = graphql`
  fragment AccordionModule on SanityPageAccordionModule {
    title {
      ...LocaleStringFragment
    }
    text {
      ...LocaleTextFragment
    }
    groups {
      subtitle {
        ...LocaleStringFragment
      }
      items {
        title {
          ...LocaleStringFragment
        }
        content {
          ...LocaleCustomListPortableText
        }
        startsOpen
      }
    }
  }
`;

export interface AccordionItem {
  title: string;
  content: RawPortableText;
  startsOpen?: boolean;
}

export interface AccordionGroup {
  subtitle?: string;
  items: Array<AccordionItem>;
}

export interface AccordionModuleProps {
  title?: string;
  text?: string;
  groups: Array<AccordionGroup>;
  className?: string;
}

export function getModuleBgColor(props: AccordionModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */

  return 'white';
}

function AccordionModule(props: AccordionModuleProps & CommonModuleProps): React.ReactElement {
  const { title, text, groups, className, moduleId, previousModuleBgColor } = props;
  const containerRef = useRef<HTMLElement | null>(null);

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: `.${styles.accordionContainer}`,
          start: 'top 80%',
        } satisfies ScrollTrigger.Vars,
      });

      tl.from(`.${styles.accordionGroup}`, {
        opacity: 0,
        y: 60,
        duration: 0.8,
        stagger: 0.8,
        ease: 'power2.out',
      });
    },
    { scope: containerRef },
  );

  // Flatten every accordion item across groups into FAQPage question/answer pairs.
  const faqItems = groups.flatMap(group =>
    group.items.map(item => ({
      question: item.title,
      answer: getPortableTextAsString(item.content),
    })),
  );

  return (
    <>
      <JsonLd data={buildFaqSchema(faqItems)} />
      <ModuleLayout
        id={moduleId}
        className={className}
        title={title}
        text={text}
        previousModuleBgColor={previousModuleBgColor}
        currentModuleBgColor={getModuleBgColor(props)}
        contentClassName={clsx(
          getModuleBgColor(props) === 'white' &&
            previousModuleBgColor === 'white' &&
            styles.topArcWithNoPaddingTop,
        )}
        moduleRef={containerRef}
      >
        <div className={styles.topArc}></div>
        <div className={styles.bottomArc}></div>
        <div className={styles.accordionContainer}>
          {groups.map((group, groupIndex) => (
            <div key={groupIndex} className={styles.accordionGroup}>
              {group.subtitle && <h3 className={styles.groupSubtitle}>{group.subtitle}</h3>}
              <div className={styles.accordionList}>
                {group.items.map((item, itemIndex) => (
                  <Accordion
                    key={itemIndex}
                    title={item.title}
                    startsOpen={item.startsOpen}
                    className={styles.accordionItem}
                  >
                    <GenericPortableText blocks={item.content} />
                  </Accordion>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ModuleLayout>
    </>
  );
}

export default AccordionModule;
